import * as React from "react";

import { pendingDisposition } from "@/lib/acd";
import { useAuth } from "@/lib/auth";
import { useAcdMe, useMyAcdSessions } from "@/lib/queries";
import { useAcdCall } from "@/softphone/acd-call";
import { useSoftphone } from "@/softphone/softphone-context";

/** A lead to fall back on when the call itself names none. Development builds pass one in. */
export type StandIn = (remote: string | null) => { leadId: string | null; looking: boolean };

const NO_STAND_IN: StandIn = () => ({ leadId: null, looking: false });

/**
 * Which lead the intake panel is about, on the call this phone is on (ACL-338).
 *
 * Three readings, in the order they become true, because no single one covers a whole call:
 *
 *   1. **The ACD's record**, while the call rings or is up. It is the only source that says why
 *      the phone rang, and `lead-card.tsx` reads it the same way.
 *   2. **The stay the rep has still to disposition**, once the call ends. The event-tracker
 *      clears the call off the rep's record the moment it is over — which is exactly when the
 *      disposition window opens — so a panel that only read (1) went blank while the rep was
 *      still finishing the questions.
 *   3. **Whatever `standIn` names**, when one is given. Nothing in production gives one: a call
 *      between two colleagues has no client, so the panel does not appear. It is how a local
 *      machine, which cannot place real outbound calls, exercises the canvas at all.
 *
 * The id is held once it is known, so a reading that briefly has none (the swap from (1) to (2),
 * a refetch in flight) does not blank a panel the rep is typing into. It is let go when the phone
 * has no call at all, which is what takes an intake off the screen.
 */
export interface IntakeLead {
  leadId: string | null;
  /** The call this is about, for the answer's own `call_id`. */
  callId: string | null;
  /** Why there is no lead, when there is none, so the panel can say which. */
  why: "no_call" | "no_lead" | "looking" | null;
  /** True when the lead is the development stand-in rather than the one the ACD named. */
  standIn: boolean;
}

/** A number as `GET /leads?q=` wants it: the digits, which is what the search matches on. */
export function digitsOf(remote: string | null | undefined): string {
  return (remote ?? "").replace(/\D+/g, "");
}

/**
 * Whether a stay the rep has still to disposition is about this leg.
 *
 * The newest stay without an outcome may be a leftover from an earlier lead call, and on a
 * rep-to-rep call it always is: taking it anyway would put a previous caller's intake on the
 * screen during a call with a colleague. Pure, so `test/intake-lead.test.ts` can pin it.
 */
export function stayIsAbout(
  stay: { channel_uuid?: string | null; session_id?: string | null } | null,
  callId: string | null,
): boolean {
  if (!stay || callId === null) return false;
  return stay.channel_uuid === callId || stay.session_id === callId;
}

/**
 * Whether the other party looks like a colleague rather than the outside world: an extension is
 * short and carries no country code, where a lead's number is E.164.
 *
 * Only decides whether the development stand-in should bother looking, so being wrong about an
 * unusual number costs one search that finds nothing.
 */
export function looksInternal(remote: string | null | undefined): boolean {
  const digits = digitsOf(remote);
  return digits.length > 0 && digits.length <= 6;
}

export function useIntakeLead(callId: string | null, standIn: StandIn = NO_STAND_IN): IntakeLead {
  const phone = useSoftphone();
  const { user } = useAuth();
  const acd = useAcdCall();

  const call = phone.calls.find((one) => one.id === callId) ?? null;
  // (1) The ACD's own record, matched to this leg the way `lead-card.tsx` matches it: an exact
  // call id, or the record is about some other call that happens to be on the phone.
  const fromAcd = acd !== null && acd.callId === callId ? acd.leadId : null;

  // (2) The stay still waiting for an outcome, which is what names the call once the ACD has let
  // it go: the live record forgets a call the moment it ends, as `DispositionView` found.
  //
  // Only ever for a leg this stay is actually about. The newest stay without an outcome may be a
  // leftover from an earlier lead call, and on a rep-to-rep call it always is — attaching that
  // lead to this call would put the wrong person's intake on the screen.
  const { data: me } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  const agent = me ? { user: me.user, domain: me.domain } : null;
  const sessions = useMyAcdSessions(agent);
  const pending = React.useMemo(() => {
    if (fromAcd !== null) return null;
    const stay = pendingDisposition(sessions.data ?? [], agent);
    return stayIsAbout(stay, callId) ? stay : null;
  }, [fromAcd, sessions.data, me?.user, me?.domain, callId]);

  const named = fromAcd ?? pending?.lead_id ?? null;

  // (3) Whatever was passed in, and only when nothing above named a lead.
  const borrowed = standIn(named === null && call !== null ? call.remote : null);
  const leadId = named ?? borrowed.leadId;

  // Held, so the panel does not blank between readings while the rep is working in it: the swap
  // from (1) to (2) at hangup has a moment with neither, and a refetch in flight has none.
  //
  // In an effect, not during render: writing a ref while rendering is read back by nothing in the
  // same pass and is wrong under a re-entrant render, which is exactly the kind of bug that shows
  // up once as a blank panel and never reproduces.
  const [held, setHeld] = React.useState<{
    leadId: string;
    callId: string | null;
    standIn: boolean;
  } | null>(null);
  const idle = phone.calls.length === 0;
  React.useEffect(() => {
    if (idle) {
      setHeld(null);
      return;
    }
    if (leadId === null) return;
    setHeld((was) =>
      was?.leadId === leadId && was.callId === callId
        ? was
        : { leadId, callId, standIn: named === null },
    );
  }, [idle, leadId, callId, named]);

  // The reading of this render wins over what is held; the held copy only covers the gaps.
  const settled =
    leadId !== null ? { leadId, callId, standIn: named === null } : idle ? null : held;
  if (settled) return { ...settled, why: null };
  if (call === null) return { leadId: null, callId: null, why: "no_call", standIn: false };
  if (borrowed.looking) return { leadId: null, callId, why: "looking", standIn: false };
  return { leadId: null, callId, why: "no_lead", standIn: false };
}
